import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useNotificationContext } from '@/components/ui/notification-provider';

const BASE_URL = import.meta.env.VITE_NOTIFICATIONS_URL;

export interface NotificationSSEOptions {
  userId: string;
  accessToken?: string;
  onMessage?: (notification: any) => void;
  onError?: (error: Event) => void;
  onOpen?: (event: Event) => void;
  onClose?: () => void;
}

export class NotificationSSE {
  private eventSource: EventSource | null = null;
  private userId: string;
  private accessToken?: string;
  private onMessage?: (notification: any) => void;
  private onError?: (error: Event) => void;
  private onOpen?: (event: Event) => void;
  private onClose?: () => void;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectDelay = 30000; // 30 seconds

  constructor(options: NotificationSSEOptions) {
    this.userId = options.userId;
    this.accessToken = options.accessToken;
    this.onMessage = options.onMessage;
    this.onError = options.onError;
    this.onOpen = options.onOpen;
    this.onClose = options.onClose;
  }

  /**
   * Connect to the SSE endpoint
   */
  connect(): void {
    try {
      // Close existing connection if any
      if (this.eventSource) {
        this.eventSource.close();
      }

      // Note: EventSource doesn't support custom headers in browsers
      // If auth is needed, pass token as query param
      const token = this.accessToken || localStorage.getItem('accessToken');
      const url = token
        ? `${BASE_URL}/notifications/sse/${this.userId}?token=${token}`
        : `${BASE_URL}/notifications/sse/${this.userId}`;

      this.eventSource = new EventSource(url);

      // Connection opened
      this.eventSource.onopen = (event) => {
        this.reconnectAttempts = 0; // Reset reconnection attempts
        this.onOpen?.(event);
      };

      // Default message handler
      this.eventSource.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          this.onMessage?.(notification);
        } catch (error) {
          console.error('[SSE] Failed to parse notification:', error);
        }
      };

      // Listen for specific event types
      this.eventSource.addEventListener('notification', (event: MessageEvent) => {
        try {
          const notification = JSON.parse(event.data);
          console.log('[SSE] New notification received:', notification);
          this.onMessage?.(notification);
        } catch (error) {
          console.error('[SSE] Error parsing notification event:', error);
        }
      });

      this.eventSource.addEventListener('notification-read', (event: MessageEvent) => {
        try {
          const notification = JSON.parse(event.data);
          console.log('[SSE] Notification marked as read:', notification);
          this.onMessage?.(notification);
        } catch (error) {
          console.error('[SSE] Error parsing notification-read event:', error);
        }
      });

      this.eventSource.addEventListener('all-read', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[SSE] All notifications marked as read:', data);
          this.onMessage?.(data);
        } catch (error) {
          console.error('[SSE] Error parsing all-read event:', error);
        }
      });

      // Connection error
      this.eventSource.onerror = (error) => {
        console.error('[SSE] Connection error:', error);

        // Close the connection
        this.eventSource?.close();
        this.eventSource = null;

        // Call error handler
        this.onError?.(error);

        // Attempt to reconnect with exponential backoff
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('[SSE] Failed to establish connection:', error);
    }
  }

  /**
   * Schedule a reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    const reconnectDelay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );

    this.reconnectAttempts += 1;

    console.log(
      `[SSE] Attempting to reconnect in ${reconnectDelay / 1000}s (attempt ${this.reconnectAttempts})...`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, reconnectDelay);
  }

  /**
   * Close the SSE connection
   */
  close(): void {

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.onClose?.();
    }
  }

  /**
   * Check if the connection is active
   */
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }
}

/**
 * Helper function to create and connect to SSE notifications
 * @param userId - The user ID to listen for notifications
 * @param callbacks - Optional callbacks for SSE events
 * @returns NotificationSSE instance
 */
export const createNotificationSSE = (
  userId: string,
  callbacks?: {
    onMessage?: (notification: any) => void;
    onError?: (error: Event) => void;
    onOpen?: (event: Event) => void;
    onClose?: () => void;
  }
): NotificationSSE => {
  const sse = new NotificationSSE({
    userId,
    ...callbacks,
  });
  sse.connect();
  return sse;
};

/**
 * React hook for SSE notifications
 */
interface UseNotificationSSEOptions {
  onNewNotification?: (data: any) => void;
  onError?: (error: Event) => void;
  enabled?: boolean;
}

export const useNotificationSSE = (options: UseNotificationSSEOptions = {}) => {
  const { onNewNotification, onError, enabled = true } = options;
  const { isEnabled: notificationsEnabled, userId } = useNotificationContext();
  const queryClient = useQueryClient();
  const sseInstanceRef = useRef<NotificationSSE | null>(null);

  useEffect(() => {
    // Don't connect if notifications are disabled or no userId
    if (!notificationsEnabled || !enabled || !userId) {
      return;
    }

    // Create SSE instance
    sseInstanceRef.current = createNotificationSSE(userId, {
      onMessage: (notification) => {
        // Invalidate queries to refresh the UI
        if (notification?.type!=='connected') {
          queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
        }

        // Call custom handler if provided
        if (onNewNotification) {
          onNewNotification(notification);
        }
      },
      onError: (error) => {
        // Call error handler if provided
        if (onError) {
          onError(error);
        }
      },
      onOpen: () => {
        console.log('[SSE Hook] Connection established');
      },
      onClose: () => {},
    });

    // Cleanup function
    return () => {
      if (sseInstanceRef.current) {
        sseInstanceRef.current.close();
        sseInstanceRef.current = null;
      }
    };
  }, [notificationsEnabled, enabled, userId, queryClient, onNewNotification, onError]);

  return {
    isConnected: sseInstanceRef.current?.isConnected() ?? false,
  };
};

